import type { RequestTransformFunction } from "mapbox-gl";
import { apiAxiosInstance } from "~/api";
import type { AuthClient } from "~/api/AuthClient";

export type WfsBody = { text: string; contentType: string };

const proxyBase = `${import.meta.env.VITE_API_HOST}/property-map/`;

export function propertyMapUrl(id: string, kind: "wfs" | "wms"): string {
  return `${proxyBase}${encodeURIComponent(id)}?kind=${kind}${kind === "wms" ? "&BBOX={bbox-epsg-3857}" : ""}`;
}

export function isPropertyMapUrl(url: string): boolean {
  try {
    const target = new URL(url, window.location.origin);
    const base = new URL(proxyBase, window.location.origin);
    return (
      target.origin === base.origin && target.pathname.startsWith(base.pathname)
    );
  } catch {
    return false;
  }
}

export async function fetchPropertyMapWfs(
  url: string,
  signal: AbortSignal,
): Promise<WfsBody> {
  const response = await apiAxiosInstance.get<string>(url, {
    signal,
    responseType: "text",
  });
  return {
    text: response.data,
    contentType: String(response.headers["content-type"] ?? ""),
  };
}

export function authenticatedMapRequest(
  authClient: Pick<AuthClient, "getFreshAuthorization">,
): RequestTransformFunction {
  return async (url, _resourceType, options) => {
    if (!isPropertyMapUrl(url)) return { url };
    const authorization = await authClient.getFreshAuthorization();
    options?.signal?.throwIfAborted();
    return { url, headers: { Authorization: authorization } };
  };
}

export function propertyMapLegendUrl(id: string): string {
  return `${proxyBase}${encodeURIComponent(id)}?kind=legend`;
}

export async function fetchPropertyMapLegend(
  url: string,
  signal: AbortSignal,
): Promise<Blob> {
  const response = await apiAxiosInstance.get<Blob>(url, {
    signal,
    responseType: "blob",
  });
  return response.data;
}
